import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { deleteCookie, getCookie, setCookie } from "../helpers/cookie";
import { SSO_COOKIE } from "../constants";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = new URLSearchParams(window.location.search);
  const cont = params.get("continue");
  if (cont) {
    setCookie(SSO_COOKIE, cont);
  }

  const handleSigninComplete = async () => {
    const { tokens } = await fetchAuthSession({ forceRefresh: true });
    const idToken = tokens?.idToken?.toString();

    if (idToken) {
      const cont = getCookie(SSO_COOKIE);
      deleteCookie(SSO_COOKIE);

      if (cont) {
        const assertUrl = new URL(cont);
        if (assertUrl.pathname.endsWith("/oauth2/authorize")) {
          assertUrl.pathname = assertUrl.pathname.replace(
            "/oauth2/authorize",
            "/oauth2/assert"
          );
        }
        assertUrl.search = "";
        assertUrl.searchParams.set("continue", cont);

        const form = document.createElement("form");
        form.method = "POST";
        form.action = assertUrl.toString();
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "idToken";
        input.value = idToken;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const u = await getCurrentUser();
      const attr = await fetchUserAttributes();
      const user = {
        ...u,
        attributes: attr,
      };
      await handleSigninComplete();
      setUser(user);
    } catch {
      setUser(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, loading, refresh }),
    [user, loading, refresh]
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
